import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, CalendarCheck, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { AdminGate } from "./events/AdminGate";

const ProviderCard = ({ id, name, status, startPath, disconnectPath, onChange }) => {
  const [busy, setBusy] = useState(false);
  const ready = id === "outlook" ? status?.connected && status?.calendar_ready : status?.connected;
  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(startPath);
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't start sign-in.");
      setBusy(false);
    }
  };
  const disconnect = async () => {
    await api.post(disconnectPath);
    onChange();
  };
  return (
    <div data-testid={`calendar-card-${id}`} className="rounded-2xl bg-white border border-black/10 p-5">
      <div className="flex items-center justify-between gap-3">
        <p className="font-display font-black text-[18px] text-brand-charcoal">{name}</p>
        <span data-testid={`calendar-status-${id}`} className={`text-[10.5px] font-bold uppercase tracking-[0.12em] ${ready ? "text-brand-teal" : "text-brand-charcoal/45"}`}>
          {ready ? "Connected" : status?.connected ? "Reconnect needed" : "Not connected"}
        </span>
      </div>
      {status?.account_email && <p className="mt-1 text-[12.5px] text-brand-charcoal/60">{status.account_email}</p>}
      {!status?.configured ? (
        <p data-testid={`calendar-not-configured-${id}`} className="mt-3 text-[12.5px] text-brand-gold-dark">App keys not added yet.</p>
      ) : ready ? (
        <button type="button" onClick={disconnect} data-testid={`calendar-disconnect-${id}`} className="mt-3 text-[11px] font-bold uppercase tracking-[0.1em] text-red-600">Disconnect</button>
      ) : (
        <>
          {status?.connected && <p className="mt-2 text-[12px] text-brand-charcoal/60">Outlook is connected for contacts/drafts — reconnect once to allow calendar access.</p>}
          <button type="button" onClick={connect} disabled={busy} data-testid={`calendar-connect-${id}`} className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white px-5 py-2.5 text-[11.5px] font-bold uppercase tracking-[0.1em] disabled:opacity-50">
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Connect {name}
          </button>
        </>
      )}
    </div>
  );
};

function Calendars() {
  const [params] = useSearchParams();
  const [g, setG] = useState(null);
  const [o, setO] = useState(null);
  const [sync, setSync] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const load = () => {
    api.get("/admin/google-calendar/status").then((r) => setG(r.data));
    api.get("/admin/outlook/status").then((r) => setO(r.data));
    api.get("/admin/calendars/status").then((r) => setSync(r.data));
  };
  useEffect(() => {
    load();
    const res = params.get("google") || params.get("outlook");
    if (res === "connected") toast.success("Calendar connected — syncing your orders now.");
    else if (res) toast.error(`Calendar sign-in ${res}. Please try again.`);
    if (res === "connected") api.post("/admin/calendars/sync").then(load).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/calendars/sync");
      if (!data.google && !data.outlook) toast.info("Connect a calendar first — nothing to sync yet.");
      else toast.success(`Synced · ${data.created_or_updated} updated · ${data.removed} removed${data.errors ? ` · ${data.errors} errors` : ""}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Sync failed.");
    } finally {
      setSyncing(false);
    }
  };
  const last = sync?.last_sync;
  return (
    <div className="min-h-screen bg-brand-cream/50" data-testid="calendars-page">
      <div className="max-w-3xl mx-auto px-4 pt-5 pb-16">
        <Link to="/admin/orders" className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.1em] text-brand-charcoal/60"><ArrowLeft className="h-3.5 w-3.5" /> Admin</Link>
        <h1 className="mt-3 font-display font-black text-4xl text-brand-charcoal flex items-center gap-3"><CalendarCheck className="h-8 w-8 text-brand-teal" /> Calendars</h1>
        <p className="mt-1 text-[14px] text-brand-charcoal/60">Pending and confirmed orders appear on your calendars automatically, update when an order changes, and disappear when it's cancelled.</p>
        <div className="mt-6 grid sm:grid-cols-2 gap-3">
          <ProviderCard id="google" name="Google Calendar" status={g} startPath="/admin/google-calendar/start" disconnectPath="/admin/google-calendar/disconnect" onChange={load} />
          <ProviderCard id="outlook" name="Outlook" status={o} startPath="/admin/outlook/start?next=calendars" disconnectPath="/admin/outlook/disconnect" onChange={load} />
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white border border-black/10 p-4">
          <p data-testid="calendar-last-sync" className="text-[12.5px] text-brand-charcoal/60">
            {last ? `Last sync ${new Date(last.at).toLocaleString()} · ${sync.linked_orders} orders tracked${last.errors ? ` · ${last.errors} errors` : ""}` : "Not synced yet."}
          </p>
          <button type="button" onClick={syncNow} disabled={syncing} data-testid="calendar-sync-now" className="inline-flex items-center gap-2 rounded-full border border-black/15 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] disabled:opacity-50">
            <RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} /> Sync now
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CalendarsPage() {
  return <AdminGate><Calendars /></AdminGate>;
}
